import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuthContext } from "../../hooks";
import { getOrgUserDetails } from "../../api/users/getOrgUserDetails";
import {
  addEmployeeWithPortalLogin,
  deleteInsytixEmployee,
  getInsytixAccess,
  getInsytixEmployees,
  getLoginEmail,
  InsytixEmployee,
  InsytixEmployeeInput,
  saveEmployeeBasicPay,
  saveEmployeeProfile,
  updateInsytixEmployee,
} from "../../api/InsytixEmployees";
import { getIndEmployeePay } from "../../api/Payslip/getIndividulaEmployeePay";
import { getUsers } from "../../api/users/getUsers";
import { getOrgUserRoles } from "../../api/users/getOrgUserRoles";
import InsytixEmployeesSc from "./index.sc";

const emptyForm: InsytixEmployeeInput = {
  name: "",
  dob: "",
  doj: "",
  p_mail: "",
  c_mail: "",
  acc_no: "",
  bank_name: "",
  basic_pay: "",
  pan_no: "",
  aadhar_no: "",
  passport_no: "",
};

const toForm = (employee?: InsytixEmployee): InsytixEmployeeInput => ({
  name: employee?.Name || "",
  dob: employee?.dob || "",
  doj: employee?.doj || "",
  p_mail: employee?.p_mail || "",
  c_mail: employee?.c_mail || "",
  acc_no: employee?.acc_no || "",
  bank_name: employee?.bank_name || "",
  basic_pay:
    employee?.basic_pay != null && employee?.basic_pay !== undefined
      ? String(employee.basic_pay)
      : "",
  pan_no: employee?.pan_no || "",
  aadhar_no: employee?.aadhar_no || "",
  passport_no: employee?.passport_no || "",
});

function Index() {
  const auth = useAuthContext();
  const orgId = auth?.userInfo?.orgs?.[0]?.id || 100000000001;
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [employees, setEmployees] = useState<InsytixEmployee[]>([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<InsytixEmployeeInput>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingEmployee, setEditingEmployee] = useState<InsytixEmployee | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [portalUsers, setPortalUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [accountForm, setAccountForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    role_id: "",
    leadOrgId: "",
  });

  const loginEmail = getLoginEmail(auth);

  useEffect(() => {
    const checkAdmin = async () => {
      if (auth?.isAuthenticating) return;
      if (auth?.userInfo?.is_admin) {
        setIsAdmin(true);
        return;
      }
      if (!loginEmail) {
        setIsAdmin(false);
        return;
      }
      try {
        const access = await getInsytixAccess(orgId, loginEmail);
        setIsAdmin(Boolean(access?.is_admin));
      } catch (error) {
        const orgUserId = auth?.userInfo?.orgs_user_id?.[0];
        if (orgId == null || orgUserId == null) {
          setIsAdmin(false);
          return;
        }
        const user = await getOrgUserDetails(orgId, String(orgUserId));
        const record = Array.isArray(user) ? user[0] : user;
        setIsAdmin(Boolean(record?.is_admin));
      }
    };
    checkAdmin();
  }, [auth?.isAuthenticating, auth?.userInfo, loginEmail, orgId]);

  const loadEmployees = async () => {
    if (orgId == null) return;
    const rows = await getInsytixEmployees(orgId);
    setEmployees(Array.isArray(rows) ? rows : []);
  };

  useEffect(() => {
    if (isAdmin) {
      loadEmployees().catch(() => toast.error("Could not load Insytix employees"));
    }
  }, [isAdmin, orgId]);

  const visibleEmployees = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return employees;
    return employees.filter((employee) =>
      [employee.employee_id, employee.Name, employee.c_mail, employee.portal_email]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [employees, search]);

  const openAdd = async () => {
    setEditingId(null);
    setAccountForm({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      role_id: "",
      leadOrgId: "",
    });
    const [people, roleRows] = await Promise.all([
      getUsers(orgId),
      getOrgUserRoles(orgId),
    ]);
    setPortalUsers(Array.isArray(people) ? people : []);
    setRoles(Array.isArray(roleRows) ? roleRows : []);
    setShowForm(true);
  };

  const openEdit = async (employee: InsytixEmployee) => {
    setEditingId(employee.employee_id);
    setEditingEmployee(employee);
    const pay = await getIndEmployeePay(orgId, employee.employee_id);
    const record = Array.isArray(pay) ? pay[0] : null;
    setForm({
      ...toForm(employee),
      basic_pay:
        record?.basic_pay != null && record?.basic_pay !== ""
          ? String(record.basic_pay)
          : toForm(employee).basic_pay,
      bank_name: employee.bank_name || record?.bank_name || "",
    });
    setShowForm(true);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const onSave = async () => {
    if (orgId == null) return;
    if (editingId && !form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const profileSaved = editingEmployee?.org_user_id
          ? await saveEmployeeProfile(orgId, String(editingEmployee.org_user_id), {
              ...form,
              linkedin: (editingEmployee as any)?.linkedin || "",
              github: editingEmployee.designation || (editingEmployee as any)?.github || "",
              facebook: (editingEmployee as any)?.facebook || "",
            }).then(() => true).catch(() => false)
          : false;
        const paySaved =
          form.basic_pay !== ""
            ? await saveEmployeeBasicPay(orgId, editingId, form.basic_pay || 0)
                .then(() => true)
                .catch(() => false)
            : false;
        const insytixSaved = await updateInsytixEmployee(orgId, editingId, form)
          .then(() => true)
          .catch(() => false);
        if (!profileSaved && !paySaved && !insytixSaved) {
          toast.error("Could not save employee");
          return;
        }
        toast.success("Employee updated");
      } else {
        if (!accountForm.first_name.trim() || !accountForm.last_name.trim() || !accountForm.email.trim()) {
          toast.error("First name, last name, and email are required");
          setSaving(false);
          return;
        }
        await addEmployeeWithPortalLogin(orgId, accountForm);
        toast.success("Employee added");
      }
      setShowForm(false);
      await loadEmployees();
    } catch (error) {
      toast.error("Could not save employee");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (employee: InsytixEmployee) => {
    if (orgId == null) return;
    const confirmed = window.confirm(
      `Delete ${employee.Name} (${employee.employee_id})?`
    );
    if (!confirmed) return;
    try {
      await deleteInsytixEmployee(orgId, employee.employee_id);
      toast.success("Employee deleted");
      await loadEmployees();
    } catch (error) {
      toast.error("Could not delete employee");
    }
  };

  if (isAdmin === null) {
    return (
      <InsytixEmployeesSc>
        <p className="message">Checking access...</p>
      </InsytixEmployeesSc>
    );
  }

  if (!isAdmin) {
    return (
      <InsytixEmployeesSc>
        <h1>Insytix employees</h1>
        <p className="message">Admin access is required to view this list.</p>
      </InsytixEmployeesSc>
    );
  }

  return (
    <InsytixEmployeesSc>
      <div className="page-header">
        <div>
          <h1>Insytix employees</h1>
        </div>
        <button className="primary" onClick={openAdd}>
          Add employee
        </button>
      </div>
      <div className="toolbar">
        <input
          className="search"
          placeholder="Search by name or ID"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      <table>
        <thead>
          <tr>
            <th>Employee ID</th>
            <th>Name</th>
            <th>Date of birth</th>
            <th>Date of joining</th>
            <th>Email</th>
            <th>Bank name</th>
            <th>Designation</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {visibleEmployees.map((employee) => (
            <tr key={employee.employee_id}>
              <td>{employee.employee_id}</td>
              <td>{employee.Name}</td>
              <td>{employee.dob || ""}</td>
              <td>{employee.doj || ""}</td>
              <td>{employee.c_mail || ""}</td>
              <td>{employee.bank_name || ""}</td>
              <td>{employee.designation || ""}</td>
              <td>
                <div className="actions">
                  <button className="secondary" onClick={() => openEdit(employee)}>
                    Edit
                  </button>
                  <button className="danger" onClick={() => onDelete(employee)}>
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal show={showForm} onHide={() => setShowForm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId ? `Edit ${editingId}` : "Add employee"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingId ? (
            <div className="row g-3">
              {(
                [
                  ["name", "Name", ""],
                  ["dob", "Date of birth", "DD-MM-YYYY"],
                  ["doj", "Date of joining", "DD-MM-YYYY"],
                  ["p_mail", "Personal email", ""],
                  ["c_mail", "Company email", ""],
                  ["acc_no", "Account number", ""],
                  ["bank_name", "Bank name", ""],
                  ["basic_pay", "Basic salary", ""],
                  ["pan_no", "PAN", ""],
                  ["aadhar_no", "Aadhaar", ""],
                  ["passport_no", "Passport", ""],
                ] as const
              ).map(([name, label, placeholder]) => (
                <Form.Group className="col-md-6" key={name}>
                  <Form.Label>{label}</Form.Label>
                  <Form.Control
                    name={name}
                    placeholder={placeholder}
                    value={form[name] || ""}
                    onChange={onChange}
                  />
                </Form.Group>
              ))}
            </div>
          ) : (
            <div className="row g-3">
              {(
                [
                  ["first_name", "First name"],
                  ["last_name", "Last name"],
                  ["email", "Email"],
                  ["phone", "Phone"],
                ] as const
              ).map(([name, label]) => (
                <Form.Group className="col-md-6" key={name}>
                  <Form.Label>{label}</Form.Label>
                  <Form.Control
                    name={name}
                    value={accountForm[name]}
                    onChange={(event) =>
                      setAccountForm((current) => ({
                        ...current,
                        [name]: event.target.value,
                      }))
                    }
                  />
                </Form.Group>
              ))}
              <Form.Group className="col-md-6">
                <Form.Label>Role</Form.Label>
                <Form.Select
                  value={accountForm.role_id}
                  onChange={(event) =>
                    setAccountForm((current) => ({
                      ...current,
                      role_id: event.target.value,
                    }))
                  }
                >
                  <option value="">Select a role</option>
                  {roles.map((role) => (
                    <option key={role.role_id} value={role.role_id}>
                      {role.role_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="col-md-6">
                <Form.Label>Reports to</Form.Label>
                <Form.Select
                  value={accountForm.leadOrgId}
                  onChange={(event) =>
                    setAccountForm((current) => ({
                      ...current,
                      leadOrgId: event.target.value,
                    }))
                  }
                >
                  <option value="">Select a manager</option>
                  {portalUsers.map((person) => (
                    <option key={person.org_user_id} value={person.org_user_id}>
                      {`${person.first_name || ""} ${person.last_name || ""}`.trim()}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </InsytixEmployeesSc>
  );
}

export default Index;
